import type { IFileUploaderProps, IFormFieldProps } from "@/ui-kit";
import { FileUploader, FormField } from "@/ui-kit";
import { useCallback } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useGetFileFromUrl } from "@/components/controls/FileInputControl/hooks/useGetFileFromUrl";
import get from "lodash/get";

type IProps = Omit<IFileUploaderProps, "files" | "onChange"> &
  Partial<IFormFieldProps> & {
    name: string;
    formElementClass?: string;
    clear?: boolean;
    onUpload?: (file: File) => void;
  };

export const FileInputControl = (props: IProps) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    clear,
    description,
    maxFiles = 1,
    onUpload,
    ...fileUploaderProps
  } = props;
  const { setValue, clearErrors, getValues } = useFormContext();
  const { errors } = useFormState();
  const files: File[] | File | undefined = useWatch({ name });
  const errorMessage = get(errors, name)?.message as string | undefined;

  const handleFileFromUrl = useCallback(
    (files: File[]) => {
      setValue(name, files);
    },
    [setValue, name],
  );

  useGetFileFromUrl(name, handleFileFromUrl);

  const normalizedFiles =
    files === undefined ? [] : Array.isArray(files) ? files : [files];

  const handleFileChange = useCallback(
    (newFiles: File[]) => {
      // Find NEW files (not from URL and not in previous list)
      const addedFiles = newFiles.filter(
        (newFile) =>
          !(newFile as File & { __originalUrl?: string }).__originalUrl &&
          !normalizedFiles.some(
            (oldFile) =>
              `${newFile.name}-${newFile.size}-${newFile.lastModified}` ===
              `${oldFile.name}-${oldFile.size}-${oldFile.lastModified}`,
          ),
      );

      addedFiles.forEach((file) => onUpload?.(file));

      // Find removed files by comparing old vs new
      const removedFiles = normalizedFiles.filter(
        (oldFile) =>
          !newFiles.some(
            (newFile) =>
              `${newFile.name}-${newFile.size}-${newFile.lastModified}` ===
              `${oldFile.name}-${oldFile.size}-${oldFile.lastModified}`,
          ),
      );

      // Sync photosUrl by removing URLs of removed files
      if (removedFiles.length > 0) {
        const urlFieldName = `${name}Url`;
        const currentUrls: string[] | string | undefined =
          getValues(urlFieldName);
        const urlsArray =
          typeof currentUrls === "string" ? [currentUrls] : currentUrls || [];

        const urlsToRemove = removedFiles
          .map((file) => (file as any).__originalUrl)
          .filter(Boolean);

        if (urlsToRemove.length > 0) {
          const updatedUrls = urlsArray.filter(
            (url) => !urlsToRemove.includes(url),
          );
          setValue(urlFieldName, updatedUrls);
        }
      }

      const nextValue = Array.isArray(files) ? newFiles : newFiles[0];
      setValue(name, nextValue);
      clearErrors(name);
    },
    [files, setValue, name, clearErrors, normalizedFiles, getValues, onUpload],
  );

  return (
    <FormField
      label={label}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
      color={fileUploaderProps.color}
    >
      <FileUploader
        {...fileUploaderProps}
        files={normalizedFiles}
        maxFiles={maxFiles}
        onChange={handleFileChange}
        renameFiles={true}
      />
    </FormField>
  );
};
